import type { LobKey } from "src/jsonForms/preScreen/types";

/**
 * A claim as the page sees it. Loose because that is what the service returns:
 * `ListClaimsOutput.claims` is `Document[]`, so there is no per-claim-type
 * element type to narrow to, and the keys present are whatever the executed
 * named query lists in its `outputFields`. Not statically checkable in either
 * direction, so a declared property name is checked by the config validator and
 * by a per-config test against a checked-in expected-attribute list instead.
 */
export type ClaimRecord = Readonly<Record<string, unknown>>;

/**
 * The single point where a config-declared property name meets an untyped
 * record, so every consumer resolves a source the same way.
 */
export function readProperty(claim: ClaimRecord, source: string): unknown {
  return claim[source];
}

export type ValueDerivationKind = "epochSecondsToDate" | "optionLabel" | "concatenate";

/**
 * Column time zone. Absent means UTC.
 *
 * Declared as a tuple with the type derived from it, as every closed value set
 * in this module is, because the validator needs the set at runtime. Writing the
 * union separately would let the two drift: a value added to the union would
 * type-check in a shipped config and then be rejected by the validator, leaving
 * the document unrenderable with nothing failing at compile time.
 */
export const DERIVATION_TIME_ZONES = ["utc", "viewerLocal"] as const;

export type DerivationTimeZone = (typeof DERIVATION_TIME_ZONES)[number];

/**
 * Epoch seconds to `YYYY-MM-DD`. Input outside 0..253402300799 inclusive, or
 * not a whole number, is treated as absent.
 */
export interface EpochSecondsToDateDerivation {
  readonly kind: "epochSecondsToDate";
  readonly timeZone?: DerivationTimeZone;
}

/** Renders the label of the option whose value equals the property, character for character. */
export interface OptionLabelDerivation {
  readonly kind: "optionLabel";
  readonly options: readonly { readonly value: string; readonly label: string }[];
  /** Rendered when no option value matches. Falls back to emptyPlaceholder when absent. */
  readonly unmatchedText?: string;
}

/**
 * Joins the parts that hold a value, in declared order, with `separator`, and
 * with no leading, trailing, or repeated separator. A part marked `required`
 * that is absent triggers the empty-value path.
 */
export interface ConcatenateDerivation {
  readonly kind: "concatenate";
  readonly parts: readonly { readonly source: string; readonly required: boolean }[];
  readonly separator: string;
}

/**
 * Single-source derivations take their input from the enclosing column's or
 * export field's `source`, so a property is never named twice. `concatenate`
 * carries its own `parts` and its enclosing `source` names the primary
 * property, which is also what the column sorts on when it declares a
 * sorting field.
 */
export type ValueDerivation =
  | EpochSecondsToDateDerivation
  | OptionLabelDerivation
  | ConcatenateDerivation;

export type SortingComparatorName = "derivedDisplayValue";

export const SORT_DIRECTIONS = ["ascending", "descending"] as const;

export type SortDirection = (typeof SORT_DIRECTIONS)[number];

/**
 * Exactly one sorting selection, a field name or a named comparator. A union
 * rather than two optional properties, so declaring both or neither does not
 * compile in a bundled config and is a named validation error in a runtime one.
 */
export type ColumnSorting =
  | { readonly kind: "field"; readonly sortingField: string }
  | { readonly kind: "comparator"; readonly comparator: SortingComparatorName };

export interface ColumnDefinition {
  readonly columnId: string;
  /** Non-empty, at most 60 characters. */
  readonly header: string;
  /**
   * A returned claim attribute name. Must appear in the executed named query's
   * `outputFields` or the column renders absent for every row, which the page
   * cannot tell apart from a claim that genuinely holds no value there.
   */
  readonly source: string;
  readonly sorting: ColumnSorting;
  readonly derivation?: ValueDerivation;
  /** Rendered when a required input is absent, null, or an empty string. Absent means render nothing. */
  readonly emptyPlaceholder?: string;
  /** Passed through to the Cloudscape column. */
  readonly width?: number;
}

export const TERM_NORMALIZATIONS = ["lowercase", "uppercase", "none"] as const;

export type TermNormalization = (typeof TERM_NORMALIZATIONS)[number];

/**
 * Rule ids live here rather than beside the predicate registry so
 * `SearchTermValidationRule` does not drag the registry into this module.
 * `searchTermRules.ts` types its registry as `Record<SearchTermRuleId, ...>`,
 * so an id cannot exist in one place and not the other.
 */
export const SEARCH_TERM_RULE_IDS = ["digitsOnly"] as const;

export type SearchTermRuleId = (typeof SEARCH_TERM_RULE_IDS)[number];

/**
 * Names a predicate that lives in code and supplies its own message. A config
 * that shipped its own regular expression would be a pattern-injection
 * surface, remotely reachable once the document comes from a backend.
 */
export interface SearchTermValidationRule {
  readonly ruleId: SearchTermRuleId;
  readonly message: string;
}

/** Label pairs for viewports at or above 690 CSS pixels and below it. */
export interface SearchableFieldLabels {
  readonly buttonText: string;
  readonly buttonTextSmall: string;
  readonly placeholder: string;
  readonly placeholderSmall: string;
}

export interface SearchableField {
  readonly fieldId: string;
  /**
   * The named query to execute for this field, sent as `queryName`. Each field
   * maps to a different secondary index, so the query varies per field rather
   * than per line of business. A free string: the set is defined in the
   * backend's per-claimType manifest and grows without a UI release, so there
   * is no allowlist to validate against.
   */
  readonly queryName: string;
  /**
   * The `queryInput` property the term is sent under. A DynamoDB attribute name
   * read straight out of the request with no field mapping layer, so it is the
   * named query's index hash key rather than a request property name of the
   * caller's choosing.
   */
  readonly queryInputProperty: string;
  readonly labels: SearchableFieldLabels;
  /** Integer, 1..100. */
  readonly maxTermLength: number;
  /** Absent means the field does not accept a date range. */
  readonly acceptsDateRange?: boolean;
  /** Absent means "none". */
  readonly normalization?: TermNormalization;
  /** 0..5 entries. */
  readonly validationRules?: readonly SearchTermValidationRule[];
  /** Metrics published on submit for this field. Absent or empty publishes none. */
  readonly metrics?: readonly MetricDeclaration[];
}

/**
 * The counter name is either a literal or a runtime value. Both forms exist
 * because the current publishers use a runtime value AS the counter name:
 * publishClaimsSearchByAlias(alias) calls publishCounterMonitor(alias, 1).
 * Naming the runtime forms makes that greppable, and narrowing a field to
 * `literal` becomes a config edit rather than a code change.
 */
export const METRIC_COUNTER_NAME_KINDS = ["literal", "searchTerm", "userSiteCode"] as const;

export type MetricCounterNameKind = (typeof METRIC_COUNTER_NAME_KINDS)[number];

/**
 * `literal` is named explicitly because it is the only form carrying a value;
 * the rest are derived from the tuple, so a kind cannot be admitted by the type
 * without also being accepted by the validator.
 */
export type MetricCounterName =
  | { readonly kind: "literal"; readonly value: string }
  | { readonly kind: Exclude<MetricCounterNameKind, "literal"> };

export interface MetricDeclaration {
  /** Katal child-publisher namespace, e.g. "claimsSearchByAlias". */
  readonly publisherName: string;
  readonly counterName: MetricCounterName;
}

export interface MetricsConfig {
  /** Published once per page load. */
  readonly pageVisited: readonly MetricDeclaration[];
  /** Published on every submit regardless of the selected field. */
  readonly searchSubmitted: readonly MetricDeclaration[];
  /** Namespace for the config retrieval metric. */
  readonly configRetrievalPublisherName: string;
}

export interface ExportField {
  /** Non-empty, at most 60 characters. Becomes the CSV header cell. */
  readonly label: string;
  /** A returned claim attribute name, under the same `outputFields` coupling as a column source. */
  readonly source: string;
  readonly derivation?: ValueDerivation;
  readonly emptyPlaceholder?: string;
}

export interface ExportConfig {
  /** 1..50 entries, in export column order. */
  readonly fields: readonly ExportField[];
  /** 1..40 characters, no whitespace. */
  readonly fileNamePrefix: string;
}

/**
 * Everything the results table renders from, so the table owns its own field
 * list the way `ExportConfig` owns the CSV's. A column set and a default sort
 * order mean nothing to any other output surface, so they live here rather than
 * at the root.
 */
export interface TablePresentation {
  /** 1..50 entries, in render order. */
  readonly columns: readonly ColumnDefinition[];
  readonly defaultSorting: {
    /** Must match a declared columnId. */
    readonly columnId: string;
    readonly direction: SortDirection;
  };
  /**
   * Integer, 1..100. Rows per rendered page only. The size of the retrieved set
   * is `SearchConfig.retrievalPageSize`, which is deliberately larger: sending
   * this value to the service would retrieve exactly one page and leave the
   * remaining claims unreachable, since nothing returns the next page token.
   */
  readonly pageSize: number;
  /** Integer, 0..pageSize. Filter and pagination appear at or above this count. */
  readonly minItemsForControls: number;
  /** Each non-empty, at most 120 characters. */
  readonly loadingText: string;
  readonly resourceName: string;
  readonly resultsHeaderText: string;
  readonly filterPlaceholder: string;
  readonly filterAriaLabel: string;
  /**
   * Appended whenever the response carries a next page token, which applies to
   * every line of business and every searchable field. Optional only so the
   * validator's label checks are unchanged.
   */
  readonly moreResultsSuffix?: string;
  /** Additionally appended when the search carried a date range. */
  readonly narrowDateRangeSuffix?: string;
}

/**
 * Presence declares the country filter; absence renders no control and sends no
 * country. The role-to-country mapping stays in `countryUtils.ts` and is
 * deliberately absent here: it decides which countries' claims a user may see,
 * so putting it in a document a backend may later serve would let a config
 * change widen data access.
 */
export interface CountryFilterConfig {
  /** The `queryInput` property carrying the selected countries. */
  readonly queryInputProperty: string;
}

export interface MySubmittedClaimsConfig {
  /** The reporter-alias named query for this claim type. */
  readonly queryName: string;
  /** The `queryInput` property carrying the signed-in user's alias. */
  readonly queryInputProperty: string;
  /** Sent as the request `pageSize`. 100 today. */
  readonly pageSize: number;
  readonly tabLabel: string;
  readonly resultsHeaderText: string;
  /** Appended when the returned count equals `pageSize`. */
  readonly limitReachedSuffix: string;
  readonly exportFileNamePrefix: string;
  readonly metrics: readonly MetricDeclaration[];
}

/**
 * The claim types the retrieval and schema-resolution operations share. A
 * closed union rather than a free string: the set is closed by the backend's
 * manifest directory, so a fourth value is a backend change before it is a
 * config change.
 */
export const CLAIM_TYPES = ["workers-compensation", "cargo", "product-liability"] as const;

export type ClaimType = (typeof CLAIM_TYPES)[number];

export interface SearchConfig {
  /** Matches the LobKey the registry keys this config under. */
  readonly lob: LobKey;
  /** Sent as the `claimType` member of every retrieval request. */
  readonly claimType: ClaimType;
  /**
   * Integer, `table.pageSize`..100. Sent as the request `pageSize`, bounding the
   * retrieved set. Held apart from `table.pageSize` so a search retrieves enough
   * claims to page through client-side: the two were once the same integer, which
   * meant a search returned exactly one renderable page and reported the rest as
   * available while offering no way to reach them. `mySubmittedClaims.pageSize`
   * is the same idea for that mode.
   */
  readonly retrievalPageSize: number;
  /** 1..10 entries, in selector order. The first is selected on mount. */
  readonly searchableFields: readonly SearchableField[];
  readonly table: TablePresentation;
  readonly exportConfig: ExportConfig;
  /**
   * The `queryInput` keys carrying the date range bounds, keyed on the named
   * query's sort-key attribute. Absent means no named query accepts bounds, in
   * which case a field declaring `acceptsDateRange` reports date-bounded search
   * as unavailable rather than issuing an unbounded request. That is a
   * legitimate config state, not a defect, so the validator does not reject it.
   */
  readonly dateRangeRequest?: {
    readonly startQueryInputProperty: string;
    readonly endQueryInputProperty: string;
  };
  readonly countryFilter?: CountryFilterConfig;
  readonly mySubmittedClaims?: MySubmittedClaimsConfig;
  readonly metrics: MetricsConfig;
  /** Test id on the results table, kept so the Cypress selectors stay stable. */
  readonly testId: string;
}

export interface SearchDateRange {
  /** ISO calendar date, YYYY-MM-DD. */
  readonly start: string;
  readonly end: string;
}

export interface SearchState {
  /** A declared fieldId. Initialized to searchableFields[0].fieldId on mount. */
  readonly selectedFieldId: string;
  /** The term as typed: truncated at maxTermLength and normalized. */
  readonly term: string;
  /** Null when no range is selected, which is the initial value. */
  readonly dateRange: SearchDateRange | null;
  /**
   * Zero means no search has been submitted, which is the default unsearched
   * state. Increments once per submission, which is also how a resolving
   * response is recognized as stale rather than current.
   */
  readonly submissionId: number;
  /** Cleared on field change and on a new submission. */
  readonly validationMessage: string | null;
}

/**
 * `submit` bumps `submissionId` and clears `validationMessage`. `reject` sets
 * `validationMessage` and leaves everything else untouched so the entered term
 * survives a rejection. `selectField` clears `term`, `dateRange`, and
 * `validationMessage` but preserves `submissionId`.
 */
export type SearchStateAction =
  | { readonly type: "selectField"; readonly fieldId: string }
  | { readonly type: "setTerm"; readonly term: string }
  | { readonly type: "setDateRange"; readonly dateRange: SearchDateRange | null }
  | { readonly type: "submit" }
  | { readonly type: "reject"; readonly message: string };
